import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { brandsService } from '@services';
import { handleApiError } from '@helpers/api';
import { accountActions, alertActions, brandsActions } from '@actions';
import { FORM_TYPE, BackgroundType, ButtonType, FontType } from '@constants/brands';
import { Modal, Content, RadioButtonsWrapper, RadioButtons } from './styles';
import Settings from './Settings';
import Appearance from './Appearance';

const Tab = {
    Settings: 0,
    Appearance: 1,
};

const EditModal = ({ selectedBrand, setSelectedBrand, modal, setModal, page, setPage, rowsPerPage, locations }) => {
    const [tab, setTab] = useState(Tab.Settings);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const BrandSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        primaryLocation: yup.array().min(1, 'validation.required'),
        backgroundColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        fontColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        buttonBackgroundColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        buttonFontColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
    });

    const resolver = useYupValidationResolver(BrandSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            primaryLocation: [],
            locations: [],
            backgroundStyle: BackgroundType.Solid,
            backgroundColor: '#8FA6AE',
            font: FontType.Rubik,
            fontColor: '#fff',
            buttonStyle: ButtonType['Softly Rounded'],
            buttonBackgroundColor: '#1F4C5C',
            buttonFontColor: '#fff',
            logo: null,
        },
    });

    useEffect(() => {
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('title', selectedBrand.title, { shouldValidate: true });
            setValue(
                'primaryLocation',
                selectedBrand.locations
                    .filter(l => l.isMainLocation)
                    .map(item => ({
                        id: `${item.id}`,
                        label: locations.find(l => l.companyId === item.id).internalName,
                    })) || [],
                {
                    shouldValidate: true,
                },
            );
            setValue(
                'locations',
                selectedBrand.locations
                    .filter(l => !l.isMainLocation)
                    .map(l => ({
                        id: [{ id: l.id, label: locations.find(loc => loc.companyId === l.id).internalName }],
                    })) || [],
            );
            const { appearance } = selectedBrand;
            setValue('backgroundStyle', appearance.backgroundStyle);
            setValue('backgroundColor', appearance.backgroundColor, { shouldValidate: true });
            setValue('font', appearance.font);
            setValue('fontColor', appearance.fontColor, { shouldValidate: true });
            setValue('buttonStyle', appearance.buttonStyle);
            setValue('buttonBackgroundColor', appearance.buttonBackgroundColor, { shouldValidate: true });
            setValue('buttonFontColor', appearance.buttonFontColor, { shouldValidate: true });
            setValue('logo', appearance.logo);
        }
    }, [selectedBrand, modal.type, setValue]);

    const onClose = () => {
        setSelectedBrand(null);
        setModal({ open: false, type: FORM_TYPE.NEW });
    };

    const refetchFirstPage = async () => {
        if (page === 0) {
            await dispatch(brandsActions.get(page, rowsPerPage));
        } else {
            setPage(0); // INFO:Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => await dispatch(brandsActions.get(page, rowsPerPage)),
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await brandsService.addOrUpdateBrand({
                title: values.title,
                locations: [
                    { id: +values.primaryLocation[0]?.id, isMainLocation: true },
                    ...values.locations.filter(l => l.id).map(l => ({ id: +l['id'][0]?.id, isMainLocation: false })),
                ],
                appearance: {
                    backgroundStyle: values.backgroundStyle,
                    backgroundColor: values.backgroundColor,
                    font: values.font,
                    fontColor: values.fontColor,
                    buttonStyle: values.buttonStyle,
                    buttonBackgroundColor: values.buttonBackgroundColor,
                    buttonFontColor: values.buttonFontColor,
                    logo: values.logo,
                },
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedBrand.id }),
            });
            dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
            REFETCH_MAP[modal.type]();
            dispatch(accountActions.get());
            setModal({ open: false, type: FORM_TYPE.NEW });
            setSelectedBrand(null);
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
        }
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={modal.type === FORM_TYPE.NEW ? t('buttons.createBrand') : t('SettingsBrands.editBrand')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <RadioButtonsWrapper>
                <RadioButtons
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: Tab.Settings, label: t('common.settings') },
                        { value: Tab.Appearance, label: t('common.appearance') },
                    ]}
                />
            </RadioButtonsWrapper>
            <FormProvider {...{ control, setValue, getValues, watch, isSubmitting }}>
                <Content>
                    {tab === Tab.Settings && (
                        <Settings locations={locations} modal={modal} selectedBrand={selectedBrand} />
                    )}
                    {tab === Tab.Appearance && <Appearance />}
                </Content>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
